import React, { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import type { CustomContactInput } from "../../../../graphql/types";
import { formatMemberPhoneNumberForDisplay, formatStaffPhoneInput } from "../../../../utils/memberContactFormatting";
import { DesignDialog, DesignDialogButton } from "../../../common/dashboard/DesignDialog";
import { SectionShell } from "../../../common/dashboard/SectionShell";
import { useCmpOrganizationNoun } from "../../../../contexts/CmpOrganizationNounContext";
import { getCustomContactErrors, normalizeCustomContact } from "./memberContactFields";
import { buildStaffErrorFallback } from "../../../../utils/userFacingError";
export { getCustomContactErrors } from "./memberContactFields";

export type CustomContactCardsProps = {
  value: CustomContactInput[];
  onChange: (contacts: CustomContactInput[]) => void;
  onSave: (contacts: CustomContactInput[], contactId?: string) => Promise<boolean>;
  baseline?: CustomContactInput[];
  onDraftDirtyChange?: (dirty: boolean) => void;
  renderFooter?: React.ReactNode | ((contact: CustomContactInput, index: number) => React.ReactNode);
  renderCredentials?: (contact: CustomContactInput, saved: boolean) => React.ReactNode;
  disabled?: boolean;
  saving?: boolean;
  error?: string | null;
  contactError?: (contactId: string) => string | undefined;
  /** Create Member (IF11 MB6): contacts are kept with the new member; no access yet. */
  createMode?: boolean;
};

const getCustomContactDisplayName = (name: string) => name.trim() ? name : "this contact";

const CustomContactFields = ({
  contact, onChange, disabled, showErrors = false, baseline, trackDirty = false,
}: {
  contact: CustomContactInput;
  onChange: (contact: CustomContactInput) => void;
  disabled: boolean;
  showErrors?: boolean;
  baseline?: CustomContactInput;
  trackDirty?: boolean;
}) => {
  const errors = showErrors ? getCustomContactErrors(contact) : { name: "", email: "" };
  const field = (
    key: "name" | "jobTitle" | "email" | "phoneNumber" | "phoneNumberExtension" | "mobilePhoneNumber",
    label: string,
    placeholder: string,
    required = false,
  ) => {
    const phone = key === "phoneNumber" || key === "mobilePhoneNumber";
    const error = key === "name" || key === "email" ? errors[key] : "";
    const id = `custom-contact-${contact.id}-${key}`;
    const currentValue = phone ? formatMemberPhoneNumberForDisplay(contact[key]) : contact[key] || "";
    const savedValue = phone ? formatMemberPhoneNumberForDisplay(baseline?.[key]) : baseline?.[key] || "";
    const dirty = trackDirty && currentValue !== savedValue;
    return (
      <div className="flex min-w-0 flex-col gap-[6px]">
        <label htmlFor={id} className="text-sm font-bold text-[#0A0A0A]">{label}{required && <span className="text-[#F0202E]">*</span>}</label>
        <input
          id={id}
          autoComplete="off"
          type={phone ? "tel" : key === "email" ? "email" : "text"}
          inputMode={phone ? "tel" : key === "phoneNumberExtension" ? "numeric" : undefined}
          value={currentValue}
          onChange={(event) => onChange({ ...contact, [key]: phone ? formatStaffPhoneInput(event.target.value) : event.target.value })}
          placeholder={placeholder}
          disabled={disabled}
          aria-required={required || undefined}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
          className={`w-full rounded-lg border bg-white px-[12px] py-[11px] text-sm leading-[normal] text-[#0A0A0A] outline-none disabled:cursor-not-allowed disabled:bg-[#F7F7F8] ${error ? "border-[#E24C4C] focus:border-[#E24C4C]" : "focus:border-[#E1A731]"} ${error ? "" : disabled ? "border-[#ECECEC]" : "border-[#9E9E9E]"} ${dirty ? "member-field-dirty" : ""}`}
          style={dirty && !error ? { outline: "2px solid #E1A731", outlineOffset: -1 } : undefined}
        />
        {error && <p id={`${id}-error`} role="alert" className="text-xs text-[#E24C4C]">{error}</p>}
      </div>
    );
  };
  return <>
    <div className="grid gap-[20px] md:grid-cols-2">
      {field("name", "Contact Name", "Enter Contact Name", true)}
      {field("jobTitle", "Title or Designation", "Enter Title or Designation")}
    </div>
    <div className="grid gap-[20px] sm:grid-cols-2 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_76px_minmax(0,1fr)]">
      {field("email", "Contact Email", "Enter Contact Email", true)}
      {field("phoneNumber", "Contact Number", "Enter Contact Number")}
      {field("phoneNumberExtension", "Ext.", "0000")}
      {field("mobilePhoneNumber", "Mobile Phone Number", "Enter Mobile Phone Number")}
    </div>
  </>;
};

const CustomContactCards = ({ value, onChange, onSave, baseline, onDraftDirtyChange, renderFooter, renderCredentials, disabled = false, saving = false, error, contactError, createMode = false }: CustomContactCardsProps) => {
  const noun = useCmpOrganizationNoun();
  const [newContact, setNewContact] = useState<CustomContactInput | null>(null);
  const [addTried, setAddTried] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [localSaving, setLocalSaving] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ action: "added" | "deleted"; name: string } | null>(null);
  const saveInProgress = useRef(false);
  const busy = saving || localSaving;
  const deleteContact = value.find((contact) => contact.id === deleteId);
  // CMP Dashboard (24) L6224 (saved cards) and L6260 (add card).
  const internalUseHelper = `This contact is for internal ${noun.singular} use and will not appear on any directories.`;
  const deleteContactDisplayName = getCustomContactDisplayName(deleteContact?.name || "");
  const displayedError = localError || contactError?.(deleteId || newContact?.id || "") || error;
  const draftDirty = !!newContact && [newContact.name, newContact.jobTitle, newContact.email,
    newContact.phoneNumber, newContact.phoneNumberExtension, newContact.mobilePhoneNumber]
    .some((field) => !!field);
  const draftDirtyCallback = useRef(onDraftDirtyChange);
  draftDirtyCallback.current = onDraftDirtyChange;
  const discardNewContact = () => {
    setNewContact(null);
    setAddTried(false);
    setLocalError(null);
  };

  useEffect(() => {
    onDraftDirtyChange?.(draftDirty);
  }, [draftDirty, onDraftDirtyChange]);
  useEffect(() => () => draftDirtyCallback.current?.(false), []);

  const persist = async (next: CustomContactInput[], action: "added" | "deleted", name: string, contactId: string) => {
    if (disabled || busy || saveInProgress.current) return;
    saveInProgress.current = true;
    setLocalSaving(true);
    setLocalError(null);
    try {
      if (!await onSave(next, contactId)) return;
      if (action === "added") { setNewContact(null); setAddTried(false); }
      else setDeleteId(null);
      setSuccess({ action, name });
    } catch {
      setLocalError(buildStaffErrorFallback("save this contact"));
    } finally {
      saveInProgress.current = false;
      setLocalSaving(false);
    }
  };

  return <>
    {value.map((contact, index) => <SectionShell
      key={contact.id}
      id={`additional-contact-${contact.id}`}
      // CMP Dashboard (21) cmAddHeading (:17030): "Additional Contact N".
      title={`Additional Contact ${index + 1}`}
      description={internalUseHelper}
      variant="design"
      contentGap={16}
      headerActions={!disabled && <button type="button" disabled={busy} title="Delete this contact" aria-label={`Delete ${getCustomContactDisplayName(contact.name)}`} onClick={() => { setLocalError(null); setDeleteId(contact.id); }} className="rounded p-[2px] text-[#F21B1B] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#F21B1B] disabled:opacity-50"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 6h18M9 6V3h6v3M5 6l1 15h12l1-15M10 10v7M14 10v7" /></svg></button>}
      footer={!disabled && (typeof renderFooter === "function" ? renderFooter(contact, index) : renderFooter)}
    >
      <CustomContactFields contact={contact} showErrors={!!(contactError ? contactError(contact.id) : error)} baseline={baseline?.find((item) => item.id === contact.id)} trackDirty={!!baseline} disabled={disabled || busy} onChange={(updated) => onChange(value.map((item) => item.id === contact.id ? updated : item))} />
      {renderCredentials?.(contact, true)}
    </SectionShell>)}
    {!disabled && (newContact ? <SectionShell
      id="add-additional-contact"
      title={`Additional Contact ${value.length + 1}`}
      description={`Enter the contact's details below. ${internalUseHelper}`}
      variant="design"
      contentGap={16}
      headerActions={<button type="button" disabled={busy} title="Delete this contact" aria-label="Delete new contact" onClick={discardNewContact} className="rounded p-[2px] text-[#F21B1B] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#F21B1B] disabled:opacity-50"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 6h18M9 6V3h6v3M5 6l1 15h12l1-15M10 10v7M14 10v7" /></svg></button>}
      footer={<div className="flex flex-wrap justify-end gap-[12px] border-t border-[#ECECEC] pt-[16px]">
        <DesignDialogButton disabled={busy} onClick={discardNewContact} className="!flex-none" style={{ backgroundColor: "#FFFFFF", color: "#0A0A0A", border: "1px solid #9E9E9E", padding: "11px 22px" }}>Cancel</DesignDialogButton>
        <DesignDialogButton disabled={busy} aria-busy={busy} onClick={() => {
          setAddTried(true);
          const errors = getCustomContactErrors(newContact);
          if (errors.name || errors.email) return;
          const contact = normalizeCustomContact(newContact);
          void persist([...(baseline || value), contact], "added", contact.name, contact.id);
        }} className="!flex-none" style={{ padding: "11px 26px" }}>Save Contact</DesignDialogButton>
      </div>}
    >
      <CustomContactFields contact={newContact} onChange={setNewContact} disabled={busy} showErrors={addTried} />
      {renderCredentials?.(newContact, false)}
      {displayedError && <p role="alert" className="text-sm text-[#E24C4C]">{displayedError}</p>}
    </SectionShell> : <button type="button" disabled={busy} onClick={() => { setNewContact({ id: uuidv4(), name: "", email: "", jobTitle: "", phoneNumber: "", phoneNumberExtension: "", mobilePhoneNumber: "" }); setAddTried(false); setLocalError(null); }} className="w-fit rounded-lg border border-[#E1A731] bg-white px-[20px] py-[12px] text-sm font-semibold text-[#E1A731] hover:bg-[#FDF8EE] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E1A731] disabled:opacity-50">+&nbsp;&nbsp;Add Another Contact</button>)}
    <DesignDialog open={!!deleteContact} onClose={() => { if (!busy) setDeleteId(null); }} title="Delete Contact" icon="trash" busy={busy}
      message={createMode
        ? <>Are you sure you want to delete <strong>{deleteContactDisplayName}</strong> from this member's contacts?</>
        : <>Are you sure you want to delete <strong>{deleteContactDisplayName}</strong> from this member's contacts? Their dashboard access will be removed and this cannot be undone.</>}
      actions={<>
        <DesignDialogButton disabled={busy} onClick={() => setDeleteId(null)} style={{ backgroundColor: "#FFFFFF", color: "#0A0A0A", border: "1px solid #9E9E9E" }}>Cancel</DesignDialogButton>
        <DesignDialogButton tone="red" disabled={busy || disabled} onClick={() => { if (deleteContact) void persist((baseline || value).filter((contact) => contact.id !== deleteContact.id), "deleted", deleteContactDisplayName, deleteContact.id); }}>Yes, Delete</DesignDialogButton>
      </>}
    >{displayedError && <p role="alert" className="mt-[12px] text-sm text-[#E24C4C]">{displayedError}</p>}</DesignDialog>
    <DesignDialog open={!!success} onClose={() => setSuccess(null)} icon="success" ariaLabel="Contact saved" message={success?.action === "added"
      ? <><strong>{success.name}</strong> has been added as an additional contact for this member.</>
      : <><strong>{success?.name}</strong> has been deleted from this member’s contacts.</>} />
  </>;
};

export default CustomContactCards;
